import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import EditableTable, {
  IEditableTableColumns,
} from '@components/EditableTable';
import {
  Button,
  Form,
  FormInstance,
  InputNumber,
  message,
  Radio,
  Select,
} from 'antd';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import { IDevice } from '@pages/Assets/Inventory/types';
import { formatCurrency, toBytes } from '@utils/formatters';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { DollarOutlined } from '@ant-design/icons';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';

type UnitType = 'B' | 'KB' | 'MB';

interface AddCreditDeviceProps {
  msisdn: string;
  processData: string | number | null;
  unit?: UnitType;
}

interface AddCreditPayloadProps {
  action: ActionTypes.ADD_CREDIT_TO_SIMCARD | ActionTypes.CREDIT_PURCHASE;
  devices: AddCreditDeviceProps[];
}

interface AddCreditProps {
  devices: IDevice[];
  isUpload?: boolean;
  form: FormInstance<any>;
  actionRole: RolesTypes | undefined;
  showContracted: boolean;
}

const useAddCredit = ({
  devices,
  isUpload,
  form,
  actionRole,
  showContracted,
}: AddCreditProps): DeviceActionProps => {
  const { t } = useTranslation(['device-actions-modal', 'device-actions']);
  const { theme } = useContext(SessionContext);
  const changedDevices: IDevice[] = Form.useWatch('changedDevices', form);
  const newItem = Form.useWatch('newItem', form);
  const isOnlyOneDevice = useMemo(() => devices?.length === 1, [devices]);
  const { keycloak } = useContext(KeycloakContext);
  const customerType = keycloak.idTokenParsed?.customerType;
  const isBroker = customerType === 'BROKER';

  const [franchiseUnit, setFranchiseUnit] = useState<UnitType>('MB');

  const [getCreditCostLoading, setGetCreditCostLoading] = useState(false);
  const [creditPurchaseCost, setCreditPurchaseCost] = useState();

  const treatedDevices = useMemo(
    () =>
      devices?.map(device => ({
        key: device.id,
        msisdn: device.msisdn,
        processData: device.processData,
      })),
    [devices],
  );

  const handleDevicesChange = useCallback(
    (items: { [key: string]: any }[]) => {
      form?.setFieldValue('changedDevices', items);
    },
    [form],
  );

  useEffect(() => {
    if (
      actionRole === rolesConstants.ADD_CREDIT_TO_SIMCARD &&
      !changedDevices
    ) {
      handleDevicesChange(treatedDevices);
    }
  }, [actionRole, changedDevices, handleDevicesChange, treatedDevices]);

  const columns = useMemo(
    (): IEditableTableColumns[] => [
      {
        key: 'msisdn',
        title: t('attributes:msisdn'),
        dataIndex: 'msisdn',
      },
      {
        title: t('attributes:value'),
        key: 'processData',
        dataIndex: 'processData',
        editable: true,
        itemInputType: 'input-number',
        inputNumberProps: {
          min: 0,
        },
      },
    ],
    [t],
  );

  const payloadToSend = useMemo((): AddCreditPayloadProps => {
    const payload: {
      action: number;
      devices: AddCreditDeviceProps[];
    } = {
      action: ActionTypes.ADD_CREDIT_TO_SIMCARD,
      devices: devices as AddCreditDeviceProps[],
    };

    if (showContracted) {
      payload.action = ActionTypes.CREDIT_PURCHASE;
    }
    payload.devices = (
      isUpload || isOnlyOneDevice ? devices : changedDevices
    )?.map(device => {
      const processData = toBytes(
        Number(isOnlyOneDevice ? newItem : device?.processData),
        franchiseUnit,
      );

      return {
        msisdn: device?.msisdn,
        processData: processData ?? 0,
      };
    });

    return payload;
  }, [
    devices,
    showContracted,
    isUpload,
    isOnlyOneDevice,
    changedDevices,
    franchiseUnit,
    newItem,
  ]);

  const calculateCreditCost = useCallback(async () => {
    setGetCreditCostLoading(true);
    try {
      const payload = {
        items: payloadToSend?.devices?.map(device => {
          const credit =
            toBytes(
              Number(isOnlyOneDevice ? newItem : device?.processData),
              franchiseUnit,
            ) ?? 0;

          return {
            msisdn: device?.msisdn,
            creditBytes: credit,
          };
        }),
      };

      const { data } = await api.post(
        '/service-proxy/sims/credit-purchase-cost',
        payload,
      );

      setCreditPurchaseCost(data?.value);
    } catch (err) {
      message.error(t('steps.add-credit.error-when-calculating'));
    }
    setGetCreditCostLoading(false);
  }, [franchiseUnit, isOnlyOneDevice, newItem, payloadToSend?.devices, t]);

  const unitOptions = ['B', 'KB', 'MB'].map(item => ({
    label: item,
    value: item,
  }));

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('steps.credit'),
        name: isOnlyOneDevice ? 'newItem' : 'changedDevices',
        content: isOnlyOneDevice ? (
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            addonAfter={
              <Select
                value={franchiseUnit}
                onChange={setFranchiseUnit}
                options={unitOptions}
                style={{ width: 64 }}
              />
            }
          />
        ) : (
          <EditableTable
            data={changedDevices}
            columns={columns}
            onChange={handleDevicesChange}
          />
        ),
      },
    ];

    if (!isOnlyOneDevice) {
      defaultSteps.unshift({
        title: t('attributes:unit'),
        name: 'unit',
        content: (
          <Radio.Group
            value={franchiseUnit}
            onChange={e => setFranchiseUnit(e.target.value)}
            optionType="button"
            options={unitOptions}
          />
        ),
      });
    }

    if (showContracted && !isBroker) {
      defaultSteps.push({
        title: t('steps.add-credit.cost'),
        name: 'cost',
        showWhenUpload: true,
        isNotRequired: true,
        content: (
          <>
            <Button
              type={creditPurchaseCost ? 'default' : 'primary'}
              icon={<DollarOutlined />}
              onClick={calculateCreditCost}
              loading={getCreditCostLoading}
              disabled={!devices || (isOnlyOneDevice && !newItem)}
            >
              {t('steps.add-credit.calculate')}
            </Button>
            {(!!creditPurchaseCost || creditPurchaseCost === 0) && (
              <div
                style={{
                  display: 'inline-flex',
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 8,
                  fontSize: '1rem',
                  border: `1px dashed ${theme?.colors?.border}`,
                  background: theme?.colors?.background1,
                  color: 'var(--primary-color)',
                }}
              >
                {formatCurrency(creditPurchaseCost, 2) ?? 'R$ 00,00'}
              </div>
            )}
          </>
        ),
      });
    }

    return defaultSteps;
  }, [
    t,
    isOnlyOneDevice,
    franchiseUnit,
    unitOptions,
    changedDevices,
    columns,
    handleDevicesChange,
    showContracted,
    isBroker,
    creditPurchaseCost,
    calculateCreditCost,
    getCreditCostLoading,
    devices,
    newItem,
    theme?.colors?.border,
    theme?.colors?.background1,
  ]);

  const onClose = useCallback(() => {
    setFranchiseUnit('MB');
    setCreditPurchaseCost(undefined);
  }, []);

  return {
    title: t(
      `device-actions:labels.mot.${
        showContracted && !isBroker
          ? 'credit-purchase'
          : rolesConstants.ADD_CREDIT_TO_SIMCARD
      }`,
    ),
    steps,
    modalWidth: isOnlyOneDevice ? 380 : 580,
    payloadToSend,
    onClose,
  };
};

export default useAddCredit;
